import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import type { S25Icons } from "../s25-icon/s25.icon.const";
import { PANEL_MOVE_DIRECTIONS, type PanelMoveDirection } from "./s25.panel.move.overlay.model";

type MoveButtonEdge = "up" | "down" | "left" | "right";

type MoveButton = {
    direction: PanelMoveDirection;
    edge: MoveButtonEdge;
    icon: S25Icons.Name;
    label: string;
};

/** UI metadata per direction — order follows {@link PANEL_MOVE_DIRECTIONS}. */
const MOVE_BUTTON_BY_DIRECTION: Record<PanelMoveDirection, Omit<MoveButton, "direction">> = {
    ArrowUp: { edge: "up", icon: "arrowUp", label: "Move up" },
    ArrowDown: { edge: "down", icon: "arrowDown", label: "Move down" },
    ArrowLeft: { edge: "left", icon: "arrowLeft", label: "Move left" },
    ArrowRight: { edge: "right", icon: "arrowRight", label: "Move right" },
};

const MOVE_BUTTONS: readonly MoveButton[] = PANEL_MOVE_DIRECTIONS.map((direction) => ({
    direction,
    ...MOVE_BUTTON_BY_DIRECTION[direction],
}));

/**
 * Edge-mounted directional controls for WCAG 2.5.7 Dragging Movements.
 * Pair with drag/keyboard move patterns (see {@link DraggableDirective} in this folder).
 * Parent decides when to show this; emits {@link move} when a direction is activated.
 */
@Component({
    selector: "s25-ng-panel-move-overlay",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconButtonComponent],
    host: {
        class: "s25-panel-move-overlay",
        "[class.s25-panel-move-overlay--compact]": "compact()",
        role: "group",
        "[attr.aria-label]": "ariaLabel()",
    },
    template: `
        @for (button of moveButtons; track button.direction) {
            <s25-ng-icon-button
                [class]="'s25-panel-move-overlay__btn s25-panel-move-overlay__btn--' + button.edge"
                [type]="button.icon"
                [round]="true"
                [size]="buttonSize()"
                [label]="false"
                [ariaLabel]="button.label"
                [tabIndex]="-1"
                (mousedown)="preventFocusSteal($event)"
                (clicked)="move.emit(button.direction)"
            />
        }
    `,
    styles: `
        :host {
            inset: 0;
            pointer-events: none;
            position: absolute;
            z-index: 20;
        }

        .s25-panel-move-overlay__btn {
            --icon-color: var(--color--link-text);
            background-color: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 50%;
            box-shadow: var(--shadow--dropdown);
            display: inline-flex;
            pointer-events: auto;
            position: absolute;
        }

        .s25-panel-move-overlay__btn--up {
            /* Nudge off center so short hosts don't stack ↑/↓ on top of each other */
            left: calc(50% - 1.35rem);
            top: 0.15rem;
            transform: translateX(-50%);
        }

        .s25-panel-move-overlay__btn--down {
            bottom: 0.15rem;
            left: calc(50% + 1.35rem);
            transform: translateX(-50%);
        }

        .s25-panel-move-overlay__btn--left {
            left: 0.15rem;
            top: 50%;
            transform: translateY(-50%);
        }

        .s25-panel-move-overlay__btn--right {
            right: 0.15rem;
            top: 50%;
            transform: translateY(-50%);
        }

        :host ::ng-deep .s25-panel-move-overlay__btn .c-iconButton {
            align-items: center;
            display: inline-flex;
            justify-content: center;
            min-height: 2.5rem;
            min-width: 2.5rem;
        }

        :host.s25-panel-move-overlay--compact ::ng-deep .s25-panel-move-overlay__btn .c-iconButton {
            min-height: 2rem;
            min-width: 2rem;
        }
    `,
})
export class S25PanelMoveOverlayComponent {
    /** Slightly smaller edge controls (e.g. short tiles). */
    readonly compact = input(false);
    readonly ariaLabel = input("Move");

    readonly move = output<PanelMoveDirection>();

    readonly moveButtons = MOVE_BUTTONS;
    readonly buttonSize = computed(() => (this.compact() ? "1.25em" : "1.5em"));

    /** Keep focus on the host selection target so parent selection chrome stays active. */
    preventFocusSteal(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }
}
